import type { LoaderFunctionArgs } from "@remix-run/node";
import { useEffect } from "react";
import { json } from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
  isRouteErrorResponse,
} from "@remix-run/react";
import { captureBoundaryError } from "~/utils/sentryLazy";
import { detectLocale } from "~/utils/locale";
import { useNonce } from "~/utils/nonce-provider";
import { optionalRetailAuth } from "~/services/auth/retailSession.server";
import prisma from "~/utils/db.server";
import { detectCountry, findMarketByCountry } from "~/services/markets/marketDetection.server";
import { getFaviconPath } from "~/services/brand/brandFavicon.server";
import {
  getCustomerIdentity,
  getGuestIdentity,
  getOrCreateVisitorId,
} from "~/services/facebook/customerIdentity.server";
import {
  normalizeCity,
  normalizeState,
  normalizeName,
  normalizeZip,
  normalizeCountry,
  normalizeEmail,
  normalizePhone,
} from "~/utils/normalizeForMeta";

// EU-27 + EEA + UK + Switzerland: the jurisdictions where analytics cookies
// need opt-IN consent. Everyone else gets the opt-out notice (see loader).
const EXPLICIT_CONSENT_COUNTRIES = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE",
  "IS", "LI", "NO", "GB", "CH",
]);

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const cookieHeader = request.headers.get("Cookie") || "";
  const match = cookieHeader.match(/(?:^|;\s*)eql_theme=([^;]*)/);
  const theme = match?.[1] || "light";
  // T1.2: detect locale server-side so <html lang> matches the language the user
  // actually sees. Required for WCAG 3.1.1 and for SEO on the Vietnamese market.
  // th chrome exists only on market surfaces (/th): default it there when
  // no explicit choice, and coerce a stray th cookie to en on the VN root.
  const detectedLocale = detectLocale(request.url, cookieHeader);
  const marketHeaderForLocale = request.headers.get("x-eql-market");
  const hasLocaleCookieRoot = /(?:^|;\s*)eql_locale=/.test(cookieHeader);
  const locale = marketHeaderForLocale
    ? hasLocaleCookieRoot
      ? detectedLocale
      : "th"
    : detectedLocale === "th"
    ? "en"
    : detectedLocale;

  // P1.11 — noindex on the staging domain. Until we flip DNS to
  // eql-apparel.com, Google can happily index test.eql-apparel.com and
  // split ranking signals between the two origins. `host` below comes
  // from the incoming Host header (Cloudflare forwards it unchanged),
  // so this turns off automatically post-cutover without any manual
  // toggle.
  const reqUrl = new URL(request.url);
  const host = reqUrl.hostname.toLowerCase();
  // Generic staging detection (2026-08-27): any test.* host of any brand
  // domain is noindexed, not just EQL's — a second brand's staging host
  // must never get indexed either.
  const isStaging = host.startsWith("test.") || host.endsWith(".fly.dev");

  // CRO 2026-06-30: hreflang alternates. The storefront serves vi + en off
  // the SAME URL (language is cookie-driven — see utils/locale.ts; there is
  // no /en/ path variant), so each alternate points back at this canonical
  // URL. This tells Google the page is available in both languages and which
  // to surface per the searcher's locale, instead of guessing from a single
  // hard-coded lang. Origin is forced https (Fly terminates TLS at the edge,
  // so request.url is http:// internally) and the staging host is excluded
  // below where these are rendered (staging is noindex anyway).
  const canonicalOrigin = reqUrl.origin.replace(/^http:\/\//, "https://");
  const canonicalPath = reqUrl.pathname;

  // Audit 2026-05-06 (compliance, item #12): cookie consent gate.
  // Until the visitor explicitly accepts, non-essential analytics
  // scripts (GA4, Clarity, Meta Pixel) are NOT rendered. The
  // `eql_cookie_consent` cookie is set client-side by CookieConsent.tsx.
  const consentMatch = cookieHeader.match(/(?:^|;\s*)eql_cookie_consent=([^;]*)/);
  const cookieConsent = consentMatch?.[1] || null;
  // CRO 2026-09-09: opt-OUT model outside the EU/EEA/UK/CH. Vietnam's
  // Decree 13 has no cookie-banner mandate, yet the explicit banner covered
  // 138 px of every mobile viewport (cart drawer + checkout included) and
  // GA4 only saw ~half of real sessions because most visitors never
  // answered. Visitors from explicit-consent countries keep the opt-in
  // gate; everyone else gets analytics on by default with a slim notice
  // and a working "Reject". Country comes from Cloudflare's CF-IPCountry
  // (null → treated as non-EU: this is a Vietnam-first store).
  const visitorCountry = detectCountry(request);
  const consentMode: "explicit" | "implied" =
    visitorCountry && EXPLICIT_CONSENT_COUNTRIES.has(visitorCountry) ? "explicit" : "implied";
  const analyticsAllowed =
    cookieConsent === "accepted" || (cookieConsent === null && consentMode === "implied");

  // 5c: partner-market surfaces (/th…) advertise with the PARTNER's ids.
  // The middleware stamps x-eql-market with the destination country; when
  // present we swap EQL's pixel/GA4 for that market's configured ids —
  // or nothing at all if the partner hasn't provided them yet. VN traffic
  // (no header) is untouched.
  let marketAnalytics: { metaPixelId: string | null; ga4MeasurementId: string | null; tiktokPixelId?: string | null } | null = null;
  const marketCountry = request.headers.get("x-eql-market");
  if (marketCountry) {
    try {
      const shopRow = await prisma.shop.findFirst({ select: { id: true } });
      const detected = shopRow ? await findMarketByCountry(shopRow.id, marketCountry) : null;
      if (detected) {
        marketAnalytics = await prisma.market.findUnique({
          where: { id: detected.marketId },
          select: { metaPixelId: true, ga4MeasurementId: true, tiktokPixelId: true },
        });
      }
      marketAnalytics = marketAnalytics ?? { metaPixelId: null, ga4MeasurementId: null, tiktokPixelId: null };
    } catch {
      marketAnalytics = { metaPixelId: null, ga4MeasurementId: null, tiktokPixelId: null };
    }
  }

  // Audit 2026-05-16 (v2 — push toward 9+/10 EMQ): Manual Advanced
  // Matching for Meta Pixel, expanded to include city/state/zip from
  // the customer's default address and a persistent eql_visitor_id
  // cookie that serves as external_id for guests.
  //
  // Previous version (commit 5b2b1ea) sent em/ph/fn/ln/external_id/country
  // and pushed logged-in EMQ from 3.0 to ~6/10. Adding ct/st/zp + a
  // stable guest external_id pushes it further:
  //   - logged-in: ~6/10 → ~8-9/10 (extra address fields)
  //   - guests: ~3/10 → ~5-6/10 (now has a stable external_id even
  //     before they ever log in or buy — Meta can join sessions)
  //
  // Why call optionalRetailAuth in the ROOT loader (it runs for every
  // request including /admin and /portal): the retail_token cookie is
  // only set for retail customers, so optionalRetailAuth returns null
  // immediately (~free) when the cookie is absent. Tiny DB roundtrip
  // only when a real retail customer is browsing. Wrapped in catch()
  // so any auth-layer failure never breaks the root layout.
  const retailAuth = analyticsAllowed
    ? await optionalRetailAuth(request).catch(() => null)
    : null;

  // Visitor ID — persistent 1y cookie minted on first analytics-consented
  // visit and reused thereafter. Only set under cookie consent (the
  // helper itself is policy-agnostic; we gate the call). Used as the
  // external_id when no logged-in customer is present — gives Meta a
  // stable cross-session handle for guests.
  // Website audit 13 Oct 2026 (item 6): the id is no longer minted here. A
  // Set-Cookie on the first response made every first visit uncacheable at
  // the edge, and a cached page carrying one visitor's id would hand the same
  // external_id to everyone. The client mints it on first paint (effect in
  // <App>); from the second request on the server reads it as before. A brand
  // new visitor's first page view has no external_id — as before, because
  // parallel loaders never saw the cookie minted in the same request anyway.
  let visitorId: string | null = null;
  if (analyticsAllowed) {
    const v = getOrCreateVisitorId(cookieHeader);
    visitorId = v.setCookieHeader ? null : v.visitorId;
  }
  const mintVisitorId = analyticsAllowed && !visitorId;

  const facebookAM: Record<string, string> = {};
  if (retailAuth?.customer) {
    // Pull the full identity (customer + default-address join) via the
    // shared helper so the AM object stays in lockstep with what CAPI
    // sends from api.fb-capi.ts / checkout.tsx.
    const id = await getCustomerIdentity(retailAuth.customer.id).catch(
      () => ({} as Awaited<ReturnType<typeof getCustomerIdentity>>),
    );
    // Audit 2026-05-17: switched to shared normalizers so the raw
    // strings the Pixel SDK SHA256-hashes client-side match BYTE-FOR-BYTE
    // what conversionsApi.server.ts hashes server-side. Previously the
    // browser path ignored Vietnamese diacritics (e.g. "Hà Nội" was
    // sent as "hànội" while CAPI hashed "ha noi" → server+browser
    // never deduped against Meta's reference identity).
    const em = normalizeEmail(id.email);              if (em) facebookAM.em = em;
    const ph = normalizePhone(id.phone);              if (ph) facebookAM.ph = ph;
    const fn = normalizeName(id.firstName);           if (fn) facebookAM.fn = fn;
    const ln = normalizeName(id.lastName);            if (ln) facebookAM.ln = ln;
    const ct = normalizeCity(id.city);                if (ct) facebookAM.ct = ct;
    const st = normalizeState(id.state);              if (st) facebookAM.st = st;
    const zp = normalizeZip(id.zipCode);              if (zp) facebookAM.zp = zp;
    const co = normalizeCountry(id.country);          if (co) facebookAM.country = co;
    if (id.externalId) facebookAM.external_id = id.externalId;
  } else if (visitorId) {
    // Guest with consent: start with visitor_id + country as the
    // anonymous baseline (gives Meta a stable cross-session handle).
    facebookAM.external_id = visitorId;
    facebookAM.country = "vn";
    // Then promote them to "phone-known" if they previously submitted
    // the phone-capture modal. Only worth the DB roundtrip when the
    // visitor cookie was ALREADY set (i.e. not a brand-new session).
    // (visitorId is only set when the cookie already existed, so this is
    // never a brand-new session.)
    {
      const guestLead = await getGuestIdentity(visitorId).catch(
        () => ({}) as Awaited<ReturnType<typeof getGuestIdentity>>,
      );
      if (guestLead.phone) {
        const ph = normalizePhone(guestLead.phone);
        if (ph) facebookAM.ph = ph;
      }
      if (guestLead.email) {
        const em = normalizeEmail(guestLead.email);
        if (em) facebookAM.em = em;
      }
    }
  }

  return json({
    theme,
    locale,
    isStaging,
    analyticsAllowed,
    mintVisitorId,
    consentMode,
    facebookAM,
    canonicalOrigin,
    canonicalPath,
    // Sister brands must not show EQL's favicon (cached 60s lookup).
    faviconPath: await getFaviconPath(),
    ENV: {
      SENTRY_DSN: process.env.SENTRY_DSN || "",
      FACEBOOK_PIXEL_ID: marketAnalytics ? marketAnalytics.metaPixelId || "" : process.env.FACEBOOK_PIXEL_ID || "",
      // Set via `flyctl secrets set GOOGLE_SITE_VERIFICATION=...` after
      // creating the GSC property and copying the meta-tag token. Empty
      // string = no tag rendered, so this is safe to leave blank.
      GOOGLE_SITE_VERIFICATION: process.env.GOOGLE_SITE_VERIFICATION || "",
      // Microsoft Clarity project ID — heatmaps + session recordings.
      // Created at https://clarity.microsoft.com, Project settings → Setup.
      // `flyctl secrets set CLARITY_PROJECT_ID=abc123def` on production.
      CLARITY_PROJECT_ID: process.env.CLARITY_PROJECT_ID || "",
      // GA4 measurement ID (G-XXXXXXX). `flyctl secrets set GA4_MEASUREMENT_ID=...`
      GA4_MEASUREMENT_ID: marketAnalytics ? marketAnalytics.ga4MeasurementId || "" : process.env.GA4_MEASUREMENT_ID || "",
      // Google Ads tag (AW-XXXXXXXXX), `flyctl secrets set GOOGLE_ADS_ID=AW-…` (2026-09-04):
      // remarketing + enhanced conversions; conversions are imported from GA4 key events.
      GOOGLE_ADS_ID: process.env.GOOGLE_ADS_ID || "",
      // TikTok: market-only (VN has no TikTok pixel today; empty = none).
      TIKTOK_PIXEL_ID: marketAnalytics ? marketAnalytics.tiktokPixelId || "" : "",
    },
  });
};

export default function App() {
  const { theme, locale, ENV, isStaging, analyticsAllowed, mintVisitorId, facebookAM, canonicalOrigin, canonicalPath, faviconPath } = useLoaderData<typeof loader>();
  // Runs once hydration has committed (14 Oct 2026). Third-party LOADERS are
  // added here, not in the server HTML: gtag.js, Clarity's tag and
  // fbevents.js each insert further <script> tags into <head> as soon as
  // they run, and as server-rendered async tags they ran BEFORE React
  // hydrated — React met head elements it never rendered (#418) and rebuilt
  // the whole page in the browser (#423) on every load. Their inline queue
  // stubs (dataLayer / clarity.q / fbq.queue / ttq) stay in the HTML, so
  // every call made before the loader arrives is still delivered. Then tell
  // entry.client to put back Cloudflare's snippet (utils/cloudflareInjection.ts).
  useEffect(() => {
    if (analyticsAllowed) {
      const add = (src: string) => {
        if (document.querySelector(`script[src="${src}"]`)) return;
        const el = document.createElement("script");
        el.async = true;
        el.src = src;
        document.head.appendChild(el);
      };
      if (ENV.GA4_MEASUREMENT_ID) add(`https://www.googletagmanager.com/gtag/js?id=${ENV.GA4_MEASUREMENT_ID}`);
      if (ENV.CLARITY_PROJECT_ID) add(`https://www.clarity.ms/tag/${ENV.CLARITY_PROJECT_ID}`);
      if (ENV.FACEBOOK_PIXEL_ID) add("https://connect.facebook.net/en_US/fbevents.js");
      const ttq = (window as any).ttq;
      if (ENV.TIKTOK_PIXEL_ID && ttq?.load) {
        ttq.load(ENV.TIKTOK_PIXEL_ID);
        ttq.page();
      }
    }
    window.dispatchEvent(new Event("eql:hydrated"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Persistent guest id for Meta matching, minted client-side on the first
  // consented visit (see the loader for why not server-side). 1 year, same
  // attributes the server used.
  useEffect(() => {
    if (!mintVisitorId) return;
    try {
      if (/(^|; )eql_visitor_id=/.test(document.cookie)) return;
      const id = typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`;
      document.cookie = `eql_visitor_id=${id}; Path=/; Max-Age=31536000; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`;
    } catch {}
  }, [mintVisitorId]);
  // CSP nonce — used by every inline <script> below + <Scripts /> below.
  // See app/utils/nonce-provider.tsx + app/entry.server.tsx for the
  // request-scoped value source.
  const nonce = useNonce();
  // Resolve server-side: if "system", default to light (client will fix on hydration)
  const serverDataTheme = theme === "dark" ? "dark" : undefined;

  return (
    // suppressHydrationWarning on <html>: the inline FOUC theme script in
    // <head> below mutates `data-theme` BEFORE React hydrates (reads
    // localStorage + matchMedia, neither of which the server has access to).
    // Server may render <html> with no data-theme; client may have it set
    // to "dark" by the time React hydrates. That's a deliberate divergence
    // for FOUC prevention — without this attribute React 18 logged a
    // hydration mismatch on every page load, which contributed to the
    // 22+ #418 cluster that broke form-event hydration on /account/login
    // and /account/register. (Audit 2026-04-25 P0 fix.)
    <html lang={locale} translate="no" suppressHydrationWarning {...(serverDataTheme ? { "data-theme": serverDataTheme } : {})}>
      <head>
        <meta charSet="utf-8" />
        {/* Do NOT set `maximum-scale=1` — it disables user zoom, failing WCAG 2.1 AA
            success criterion 1.4.4 (Resize Text). Users with low vision must be able
            to magnify content. */}
        {/* viewport-fit=cover lets layout extend into iOS safe-area zones
            (notch / home indicator) so we can use env(safe-area-inset-*)
            for sticky elements. Audit 2026-05-06. */}
        <meta name="viewport" content="width=device-width,initial-scale=1,viewport-fit=cover" />
        {/* Audit 2026-05-14 (Clarity #418/#423 hydration mismatch hunt):
            We serve content in the customer's chosen locale (VI or EN, via
            the eql_locale cookie + LocaleSelectModal). Chrome/Safari/Edge
            mobile auto-translate then mutates the DOM BEFORE React hydrates
            — every translated text node breaks hydration with React error
            #418 ("text content did not match"). Clarity 7-day data:
            83% of 511 JS errors were #418, concentrated on VN-mobile.
            `notranslate` tells the browser's translate UI to skip the
            page. Customers who want a different language use OUR built-in
            locale switcher (header globe icon → toggles to EN/VI). */}
        <meta name="google" content="notranslate" />
        {/* Audit 2026-05-15: iOS Safari + iOS WebViews (Instagram /
            Facebook / Google in-app browsers) ALSO mutate the DOM
            before React hydrates via "data detectors" — phone numbers,
            dates, addresses, and emails in our HTML get auto-wrapped
            in `<a>` tags. Same hydration-mismatch failure mode as
            Chrome's auto-translate, but `notranslate` doesn't disable
            data detectors. `format-detection` is the iOS-specific
            opt-out per Apple's docs, and Clarity confirmed 18 of the
            19 #418-triggering sessions today were iOS browsers (the
            single Chrome desktop session was admin-only). Pairs with
            `notranslate` above — together they cover the two known
            pre-hydration DOM mutation vectors on this storefront. */}
        <meta
          name="format-detection"
          content="telephone=no, date=no, address=no, email=no"
        />
        {/* Keep staging out of Google's index while we share the domain. */}
        {isStaging ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : null}
        {/* CRO 2026-06-30: hreflang alternates + og:locale so the language
            of the content (cookie-driven vi/en, default vi) is declared to
            search + social crawlers, replacing the previous hard-coded
            English assumption. Same URL serves both languages, so each
            alternate is self-referential. Suppressed on staging (noindex). */}
        {!isStaging && canonicalOrigin && canonicalPath ? (
          canonicalPath === "/th" || canonicalPath.startsWith("/th/") ? (
            // Market surface (Thai store): self-referential th alternate —
            // catalogs differ from the VN root, so no cross-market
            // equivalence is declared.
            <link rel="alternate" hrefLang="th-TH" href={`${canonicalOrigin}${canonicalPath}`} />
          ) : (
            <>
              <link rel="alternate" hrefLang="vi" href={`${canonicalOrigin}${canonicalPath}`} />
              <link rel="alternate" hrefLang="en" href={`${canonicalOrigin}${canonicalPath}`} />
              <link rel="alternate" hrefLang="x-default" href={`${canonicalOrigin}${canonicalPath}`} />
            </>
          )
        ) : null}
        <meta property="og:locale" content={locale === "en" ? "en_US" : "vi_VN"} />
        {locale === "en" ? (
          <meta property="og:locale:alternate" content="vi_VN" />
        ) : (
          <meta property="og:locale:alternate" content="en_US" />
        )}
        {/* Google Search Console verification — only emits when env is set. */}
        {ENV.GOOGLE_SITE_VERIFICATION ? (
          <meta
            name="google-site-verification"
            content={ENV.GOOGLE_SITE_VERIFICATION}
          />
        ) : null}
        {/* Favicon — EQL keeps public/favicon.png (the monogram pulled
            from /cdn/shop/files/eql-6-01-smaller-white-v2.png, Apr 30
            2026); sister brands get /brand-favicon.svg so the EQL mark
            never shows in their tabs. */}
        <link rel="icon" type={faviconPath.endsWith(".svg") ? "image/svg+xml" : "image/png"} href={faviconPath} sizes="any" />
        <link rel="apple-touch-icon" href={faviconPath} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        {/* Audit 2026-05-13 (Clarity CLS=0.48 — POOR): swapped
            `display=swap` → `display=optional`. With swap, the browser
            renders system-font fallback first, then re-renders the
            page with DM Sans once it arrives — every such swap is a
            layout shift contributing to CLS. With `optional`, the
            browser uses the fallback IF the font isn't ready in
            ~100ms, and then NEVER swaps mid-pageload. Trade-off: on
            slow connections the visitor sees fallback fonts for that
            page load (cached on subsequent pageloads). Acceptable for
            CLS-zero. Preconnect + preload below keep the happy path
            fast enough that most visitors see DM Sans on first paint. */}
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,300;1,9..40,400&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=optional"
        />
        {/* Inline script to prevent FOUC: applies dark mode before paint */}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `(function(){try{var t=document.cookie.match(/(?:^|;\\s*)eql_theme=([^;]*)/);var v=t?t[1]:localStorage.getItem("eql_theme")||"light";var r=v==="dark"?"dark":v==="system"&&window.matchMedia("(prefers-color-scheme:dark)").matches?"dark":"light";if(r==="dark")document.documentElement.setAttribute("data-theme","dark");else document.documentElement.removeAttribute("data-theme");}catch(e){}})();`,
          }}
        />
        {/* ─── Google Analytics 4 (gtag.js) — only if GA4_MEASUREMENT_ID is set
            AND the visitor has accepted analytics cookies (audit 2026-05-06). */}
        {analyticsAllowed && ENV.GA4_MEASUREMENT_ID && (
          <>
            {/* gtag.js itself is added after hydration (see the App effect). */}
            <script
              nonce={nonce}
              dangerouslySetInnerHTML={{
                __html: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${ENV.GA4_MEASUREMENT_ID}',{anonymize_ip:true});${ENV.GOOGLE_ADS_ID ? "gtag('config','" + ENV.GOOGLE_ADS_ID + "',{allow_enhanced_conversions:true});" : ''}`,
              }}
            />
          </>
        )}
        {/* ─── Microsoft Clarity — only if CLARITY_PROJECT_ID is set ───
            Audit 2026-04-26: split into two tags. Previously this was a
            single inline script that did `document.createElement("script")
            + parentNode.insertBefore(t, y)` synchronously — which mutates
            the <head> DOM BEFORE React hydrates. React then walks the
            DOM, finds extra <script> nodes that aren't in its virtual
            tree, and logs ~22 hydration-mismatch errors (#418) across
            <head> children plus a #423 recovery on <body>. Real-browser
            test on 2026-04-25 reproduced this site-wide.
            By rendering the loader <script src=...> tag declaratively
            via React, it's part of the virtual tree → SSR HTML matches
            → no mismatch. The init <script> just sets up the queue
            function (no DOM injection) so it's safe inline. */}
        {analyticsAllowed && ENV.CLARITY_PROJECT_ID && (
          <>
            <script
              nonce={nonce}
              dangerouslySetInnerHTML={{
                __html: `window.clarity=window.clarity||function(){(window.clarity.q=window.clarity.q||[]).push(arguments)};`,
              }}
            />
            {/* The Clarity tag script is added after hydration (App effect). */}
          </>
        )}
        {/* ─── Meta Pixel base code (only if FACEBOOK_PIXEL_ID is set) ───
            Same fix as Clarity above: split the loader <script src> out
            of the inline init code so React owns the script tag and no
            DOM injection happens before hydration. The init script
            populates the fbq queue, sets up `init` and `PageView`, all
            of which queue commands until the loader script downloads.

            Audit 2026-05-16, two enrichments wired into this script:
              1. fbclid → _fbc cookie capture (belt-and-braces backup for
                 Meta's auto-capture, which can miss on slow connections,
                 CSP edge cases, or when fbevents.js loads after the user
                 has already navigated away from the fbclid landing URL).
                 Cookie format per Meta spec: fb.1.{unix_ms}.{fbclid}.
                 Skipped if _fbc is already set.
              2. Manual Advanced Matching object passed as the 2nd arg to
                 fbq('init', PIXEL_ID, {em, ph, fn, ln, external_id,
                 country}). Pixel SDK SHA256-hashes these client-side
                 before sending to Meta. Lifts PageView EMQ for any
                 logged-in customer from 3.0/10 → ~7/10 (the previous
                 Automatic AM only caught 4% via DOM scanning). Object
                 is JSON-stringified server-side; values are sanitized
                 already in the loader. JSON.stringify on plain object
                 values is safe to inline inside <script> as long as
                 values don't contain "</script>" — defensively
                 escaping "<" → "\\u003c" anyway. */}
        {analyticsAllowed && ENV.TIKTOK_PIXEL_ID && (
          <script
            nonce={nonce}
            dangerouslySetInnerHTML={{
              __html: `!function (w, d, t) {w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)}(window, document, 'ttq');}(window, document, 'ttq');`,
            }}
          />
        )}
        {analyticsAllowed && ENV.FACEBOOK_PIXEL_ID && (
          <>
            <script
              nonce={nonce}
              dangerouslySetInnerHTML={{
                // Audit 2026-05-17: REMOVED the inline `fbq('track','PageView')`
                // call from this init script. PageView is now fired from
                // usePageviewTracker (client-side useEffect) with a
                // session-unique event_id that is mirrored to /api/fb-capi
                // server-side for full identity enrichment + dedup. Keeping
                // the inline call here would have caused double-counting
                // (Meta would see one PageView from auto-fire + another
                // from useEffect with mismatched event_ids → 2 events).
                // The noscript <img> fallback below still covers no-JS
                // visitors with a server-side PageView ping.
                __html: `(function(){try{var u=new URLSearchParams(window.location.search);var f=u.get('fbclid');if(!f)return;if(document.cookie.indexOf('_fbc=')>=0)return;document.cookie='_fbc=fb.1.'+Date.now()+'.'+f+'; path=/; max-age=7776000; SameSite=Lax';}catch(e){}})();!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[]}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${ENV.FACEBOOK_PIXEL_ID}'${Object.keys(facebookAM).length > 0 ? `,${JSON.stringify(facebookAM).replace(/</g, "\\u003c")}` : ""});`,
              }}
            />
            {/* fbevents.js is added after hydration (App effect). */}
          </>
        )}
        {analyticsAllowed && ENV.FACEBOOK_PIXEL_ID && (
          <noscript>
            <img
              height="1"
              width="1"
              style={{ display: "none" }}
              src={`https://www.facebook.com/tr?id=${ENV.FACEBOOK_PIXEL_ID}&ev=PageView&noscript=1`}
              alt=""
            />
          </noscript>
        )}
        <Meta />
        <Links />
      </head>
      <body>
        <Outlet />
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(ENV)};`,
          }}
        />
        {/*
         * T3.35: Web Vitals beacon. We use the native PerformanceObserver
         * API to capture LCP, CLS, and TTFB (no `web-vitals` npm package
         * needed, keeping bundle size flat). Metrics are sent with
         * navigator.sendBeacon so they survive page unloads. The backend
         * endpoint is /api/web-vitals which already persists to the DB.
         */}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `
(function(){
  try {
    var deviceType = /Mobi|Android|iPhone/i.test(navigator.userAgent) ? "mobile" : "desktop";
    function rate(name, v) {
      if (name === "LCP") return v <= 2500 ? "good" : v <= 4000 ? "needs-improvement" : "poor";
      if (name === "CLS") return v <= 0.1 ? "good" : v <= 0.25 ? "needs-improvement" : "poor";
      if (name === "TTFB") return v <= 800 ? "good" : v <= 1800 ? "needs-improvement" : "poor";
      // INP (Interaction to Next Paint) replaced FID as the responsiveness
      // Core Web Vital in March 2024. Google thresholds: good ≤ 200ms,
      // needs-improvement ≤ 500ms, poor > 500ms.
      if (name === "INP") return v <= 200 ? "good" : v <= 500 ? "needs-improvement" : "poor";
      return "good";
    }
    function send(name, value) {
      try {
        var body = JSON.stringify({
          name: name,
          value: value,
          rating: rate(name, value),
          pathname: location.pathname,
          deviceType: deviceType
        });
        if (navigator.sendBeacon) navigator.sendBeacon("/api/web-vitals", body);
        else fetch("/api/web-vitals", { method: "POST", body: body, keepalive: true, headers: { "Content-Type": "application/json" } });
      } catch(e) {}
    }
    // LCP — take the largest entry before page hide
    if ("PerformanceObserver" in window) {
      var lcpValue = 0;
      try {
        var lcpObs = new PerformanceObserver(function(list) {
          var entries = list.getEntries();
          var last = entries[entries.length - 1];
          if (last) lcpValue = last.startTime;
        });
        lcpObs.observe({ type: "largest-contentful-paint", buffered: true });
      } catch(e) {}
      // CLS — accumulate session-scoped layout shifts
      var clsValue = 0;
      try {
        var clsObs = new PerformanceObserver(function(list) {
          list.getEntries().forEach(function(entry) {
            if (!entry.hadRecentInput) clsValue += entry.value;
          });
        });
        clsObs.observe({ type: "layout-shift", buffered: true });
      } catch(e) {}
      // INP (Interaction to Next Paint) — Google new responsiveness CWV
      // since March 2024 (replaced FID). We track the WORST interaction
      // duration across the session, which is what Google own field-
      // data tooling does. event-timing entries with interactionId > 0
      // are user interactions (clicks, key presses); duration is the
      // time from input to next paint. Audit 2026-05-17: added because
      // our prod telemetry showed LCP CLS TTFB but missing INP -- could
      // not measure mobile jank. Now we can.
      var inpValue = 0;
      try {
        var inpObs = new PerformanceObserver(function(list) {
          list.getEntries().forEach(function(entry) {
            if (entry.interactionId && entry.duration > inpValue) {
              inpValue = entry.duration;
            }
          });
        });
        inpObs.observe({ type: "event", buffered: true, durationThreshold: 16 });
      } catch(e) {}
      // Flush on page hide so we capture final LCP + CLS + INP
      addEventListener("visibilitychange", function() {
        if (document.visibilityState === "hidden") {
          if (lcpValue > 0) send("LCP", Math.round(lcpValue));
          send("CLS", Math.round(clsValue * 1000) / 1000);
          if (inpValue > 0) send("INP", Math.round(inpValue));
        }
      }, { once: true });
    }
    // TTFB — from navigation timing
    try {
      var navEntry = performance.getEntriesByType && performance.getEntriesByType("navigation")[0];
      if (navEntry && navEntry.responseStart > 0) {
        send("TTFB", Math.round(navEntry.responseStart));
      }
    } catch(e) {}
  } catch(e) {}
})();
            `.trim(),
          }}
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  captureBoundaryError(error);
  // T1.2: ErrorBoundary runs before the loader resolves, so we can't use the
  // same dynamic locale detection as <App />. Defaulting to "vi" since the VN
  // market is the primary audience; English fallback is acceptable on the
  // rare occasion an error fires before locale detection.
  //
  // Branded 500 vs 404 messaging: the storefront catch-all route
  // (_storefront.$.tsx) has its own 404 page so most 404s never reach this
  // ErrorBoundary. When they do (e.g. /admin/* 404), we still want a
  // reasonable brand-consistent fallback rather than Remix's stock error.
  const isRouteErr = isRouteErrorResponse(error);
  const status = isRouteErr ? error.status : 500;
  const is404 = status === 404;
  const heading = is404 ? "404 — Trang không tồn tại" : "Đã có lỗi xảy ra";
  const subheading = is404
    ? "Page not found"
    : "Something went wrong on our end.";
  const message = is404
    ? "Trang bạn tìm kiếm không tồn tại hoặc đã bị xoá. Vui lòng kiểm tra lại đường dẫn hoặc quay về trang chủ."
    : "Chúng tôi đã ghi nhận sự cố và đang xử lý. Vui lòng thử lại sau hoặc liên hệ với chúng tôi nếu cần trợ giúp ngay.";
  const messageEn = is404
    ? "The page you're looking for doesn't exist or has been moved. Check the URL or head back to the homepage."
    : "We've logged the issue and are looking into it. Please try again, or contact us if you need immediate help.";
  return (
    <html lang="vi">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* Brand-neutral (2026-09-01): this boundary also renders on
            unmatched URLs, where NO loader runs — the brand is unknowable
            server-side, and stamping EQL marks on a sister-brand domain
            is a secrecy leak. */}
        <title>{`${status} — ${subheading}`}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=optional"
        />
      </head>
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          fontFamily: '"DM Sans", system-ui, sans-serif',
          backgroundColor: "#fafaf8",
          color: "#1a1a1a",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          style={{
            padding: "1.25rem 1.5rem",
            borderBottom: "1px solid #eaeaea",
            backgroundColor: "#ffffff",
          }}
        />
        <main
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem 1.5rem",
          }}
        >
          <div style={{ maxWidth: 520, textAlign: "center" }}>
            <p
              style={{
                fontSize: "0.6875rem",
                fontWeight: 500,
                letterSpacing: "0.2em",
                color: "#8a8a8a",
                marginBottom: "1.25rem",
                textTransform: "uppercase",
              }}
            >
              {status} {subheading}
            </p>
            <h1
              style={{
                fontSize: "1.75rem",
                fontWeight: 500,
                letterSpacing: "-0.01em",
                marginBottom: "1rem",
                lineHeight: 1.2,
              }}
            >
              {heading}
            </h1>
            <p style={{ color: "#555", lineHeight: 1.55, marginBottom: "0.5rem" }}>
              {message}
            </p>
            <p style={{ color: "#888", lineHeight: 1.55, fontSize: "0.875rem", marginBottom: "2rem" }}>
              {messageEn}
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: "0.75rem", flexWrap: "wrap" }}>
              <a
                href="/"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#1a1a1a",
                  color: "#fff",
                  textDecoration: "none",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                Về trang chủ
              </a>
              <a
                href="/products"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "transparent",
                  color: "#1a1a1a",
                  textDecoration: "none",
                  border: "1px solid #1a1a1a",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                Cửa hàng
              </a>
              <a
                href="/contact"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "transparent",
                  color: "#555",
                  textDecoration: "none",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                Liên hệ
              </a>
            </div>
          </div>
        </main>
      </body>
    </html>
  );
}
